import {
	businessDescriptionValidation,
	type IKybDetails,
	type IKybDirector,
	type IUseFormData,
	urlValidation,
} from 'BreetHelpers';
import type { useAppDispatch } from 'BreetRedux';
import type { ReactNode } from 'react';
import * as yup from 'yup';

type FileOrString = string | FileList;

const fileOrStringSchema = (fieldLabel: string, maxSizeMB = 10) =>
	yup
		.mixed<FileOrString>()
		.required(`${fieldLabel} is required`)
		.test(
			'file-or-string',
			`${fieldLabel} is required`,
			(value) => (typeof value === 'string' && value.trim().length > 0) || (value instanceof FileList && value.length > 0)
		)
		.test('file-size', `File size must be less than ${maxSizeMB}MB`, (value) => {
			if (value instanceof FileList) {
				return Array.from(value).every((file) => file.size <= maxSizeMB * 1024 * 1024);
			}
			return true;
		});

const fileOrStringSchemaNotRequired = (fieldLabel: string, maxSizeMB = 10) =>
	yup
		.mixed<FileOrString>()
		.test(
			'file-or-string',
			`${fieldLabel} must be a valid file or string`,
			(value) =>
				value == null ||
				value === '' ||
				(typeof value === 'string' && value.trim().length > 0) ||
				(value instanceof FileList && value.length > 0)
		)
		.test('file-size', `File size must be less than ${maxSizeMB}MB`, (value) => {
			if (value instanceof FileList) {
				return Array.from(value).every((file) => file.size <= maxSizeMB * 1024 * 1024);
			}
			return true;
		});

export const KybFormDataAboutSchema = yup.object({
	companyName: yup
		.string()
		.required('Business name is required')
		.min(4, 'Business name must be at least 4 characters')
		.max(50, 'Business name must be at most 50 characters')
		.matches(/^[a-zA-Z0-9 ]+$/, {
			message: 'Business name can only contain letters, numbers and spaces',
			excludeEmptyString: true,
		}),

	officialEmail: yup.string().email('Enter a valid email address').required('Email is required'),

	country: yup.string().required('Country is required'),

	proofOfAddress: fileOrStringSchema('Proof of address'),

	taxId: yup.string().min(1, 'Tax Identification Number is required').required('Tax Identification Number is required'),

	officialTelephone: yup
		.string()
		.required('Phone number is required')
		.matches(/^\+?(233|234)/, 'Phone number must start with a valid country code (233 or 234).')
		.test(
			'valid-length',
			'Nigerian phone number must be 10 digits after the country code (or 11 with a leading zero).',
			(value) => {
				if (!value) return true;
				const withoutPlus = value.startsWith('+') ? value.substring(1) : value;
				if (withoutPlus.startsWith('234')) {
					// 234 + 10-digit local (13), or 234 + leading-zero 11-digit local (14) — both accepted.
					return withoutPlus.length === 13 || withoutPlus.length === 14;
				}
				return true;
			}
		)
		.test(
			'valid-length',
			'Ghanaian phone number must be 9 digits after the country code (or 10 with a leading zero).',
			(value) => {
				if (!value) return true;
				const withoutPlus = value.startsWith('+') ? value.substring(1) : value;
				if (withoutPlus.startsWith('233')) {
					// 233 + 9-digit local (12), or 233 + leading-zero 10-digit local (13) — both accepted.
					return withoutPlus.length === 12 || withoutPlus.length === 13;
				}
				return true;
			}
		),
	evidenceOfRegistration: fileOrStringSchema('Evidence of Registration or Incorporation'),
	memart: fileOrStringSchema('Memorandum and Articles of Association'),
	extraDoc: fileOrStringSchemaNotRequired('Extra Document'),
	statusReport: fileOrStringSchema('Status Report'),
	websiteUrl: urlValidation(true).optional(),
	businessDescription: businessDescriptionValidation,
});

export const KybFormDataAcknowledgementSchema = yup.object({
	acknowledged: yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
});

export const KybFormDataDirectorItemSchema = yup.object({
	firstName: yup.string().min(3, 'First name must be at least 3 characters').required('First name is required'),

	lastName: yup.string().min(3, 'Last name must be at least 3 characters').required('Last name is required'),

	fullHomeAddress: yup.string().min(10, 'Home address must be at least 10 characters').required('Home address is required'),

	recentAddressProof: fileOrStringSchema('Proof of address'),

	meansOfID: fileOrStringSchema('Identification doc'),
});

export const KybFormDataDirectorSchema = yup
	.array()
	.of(KybFormDataDirectorItemSchema)
	.min(1, 'At least one director is required')
	.max(3, 'Maximum of 3 directors is allowed')
	.required('Directors information is required');

export const KybFormDirectorsSchema = yup.object({
	director: KybFormDataDirectorSchema,
});

export type KYBSchemaType =
	| IUseFormData<typeof KybFormDirectorsSchema>
	| IUseFormData<typeof KybFormDataAboutSchema>
	| IUseFormData<typeof KybFormDataAcknowledgementSchema>;

export interface IKybFormContext {
	formDataContextProps: KYBSchemaType;
	formData: Partial<IKybDetails> | undefined;
	dispatch: ReturnType<typeof useAppDispatch>;
	directors: IKybDirector[];
}

export interface KybFormProviderProps {
	children: ReactNode;
	formData?: Partial<IKybDetails>;
}

type KybFormDataDirectorItemSchemaType = yup.InferType<typeof KybFormDataDirectorItemSchema>;

export const EMPTYDIRECTOR: KybFormDataDirectorItemSchemaType = {
	firstName: '',
	lastName: '',
	fullHomeAddress: '',
	meansOfID: '',
	recentAddressProof: '',
};

export const kybFormStages = [
	{
		title: 'About Company',
		subTitle: 'Please tell us about your company.',
	},
	{
		title: "Director's Information",
		subTitle: "Please tell us about your company's director(s).",
	},
	{
		title: 'Acknowledgement',
		subTitle: 'Carefully read and agree to our terms and conditions.',
	},
] as const;

export const kybFormStepMap: Record<string, string> = {
	'/complete-kyb/about': 'About Company',
	'/complete-kyb/directors': "Director's Information",
	'/complete-kyb/acknowledgement': 'Acknowledgement',
};
